import { useState } from 'react'
import { TableShell } from '../../components/molecules/TableShell'
import { Alert } from '../../components/atoms/Alert'
import { Button } from '../../components/atoms/Button'
import { InlineRupeeEdit } from '../../components/molecules/InlineRupeeEdit'
import { Select } from '../../components/atoms/Select'
import { Spinner } from '../../components/atoms/Spinner'
import { Th, Td } from '../../components/molecules/Table'
import { Paginator } from '../../components/molecules/Paginator'
import { ProductVariantMaterialsEditModal } from './ProductVariantMaterialsEditModal'
import { CloneMaterialsModal } from './CloneMaterialsModal'
import {
  PRODUCTFILTERALL,
  useProductPricing,
  type ProductVariantMaterialsSummaryDto
} from '../../../viewmodels/useProductPricing'
import { useAuth } from '../../../context/AuthContext'
import { SCREENCODES } from '../../../constants/screen.constants'

/**
 * /products/pricing — Product Pricing screen (formerly "Product
 * Materials"). Lists variants with their baseline cost.
 *
 * Make variants: cost rolls up from the per-variant (raw material,
 * supplier, quantity) links set in the edit modal — two variants of one
 * product can use different materials, so the mapping is per-VARIANT.
 * Buy variants: no materials; the operator types the baseline cost in the
 * Preview-cost cell (Save writes preview, Accept applies it).
 */
export function ProductPricingPage(): JSX.Element {
  const vm = useProductPricing()
  const { can } = useAuth()
  const canEdit = can(SCREENCODES.PRODUCTPRICING, 'edit')
  const [editTarget, setEditTarget] = useState<ProductVariantMaterialsSummaryDto | null>(null)
  const [acceptingId, setAcceptingId] = useState<string | null>(null)
  const [acceptError, setAcceptError] = useState<string | null>(null)
  const [cloneOpen, setCloneOpen] = useState(false)
  const [cloneNotice, setCloneNotice] = useState<string | null>(null)

  const selectedProductName =
    vm.products.find((p) => p.id === vm.productFilter)?.name ?? ''

  // A specific product whose variants are all Buy → it has no materials,
  // so "Clone materials" is meaningless. (Unfiltered view never enables
  // Clone — it requires a single product selected.)
  const selectedIsBuy =
    vm.productFilter !== PRODUCTFILTERALL &&
    vm.items.length > 0 &&
    vm.items.every((r) => r.source_type === 'buy')

  const handleAccept = async (row: ProductVariantMaterialsSummaryDto): Promise<void> => {
    setAcceptError(null)
    setAcceptingId(row.variant_id)
    const res = await vm.acceptVariant(row.variant_id)
    if (!res.ok) {
      setAcceptError(res.message)
      setAcceptingId(null)
      return
    }
    // The cost-rollup worker recomputes asynchronously; give it a moment
    // then reload so the new baseline cost shows.
    window.setTimeout(() => {
      void vm.reload()
      setAcceptingId(null)
    }, 1200)
  }

  return (
    <div className="space-y-6">
      <header className="flex items-center justify-between gap-4">
        <h1 className="font-heading text-h2 text-charcoal">Product Pricing</h1>
        <div className="text-body-sm text-text-secondary">
          Edit a variant to set its (raw material, supplier, qty) links, then click
          <span className="font-medium"> Accept</span> on that variant to copy its
          baseline cost onto it.
        </div>
      </header>

      {vm.error !== null ? (
        <Alert tone="error">
          {vm.error}{' '}
          <button
            type="button"
            className="underline underline-offset-2 hover:text-charcoal"
            onClick={() => void vm.reload()}
          >
            Retry
          </button>
        </Alert>
      ) : null}

      {acceptError !== null ? <Alert tone="error">{acceptError}</Alert> : null}
      {cloneNotice !== null ? <Alert tone="success">{cloneNotice}</Alert> : null}

      {vm.products.length > 0 ? (
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2">
            <label htmlFor="pm-product-filter" className="text-body-sm text-text-secondary">
              Product
            </label>
            <div className="w-64">
              <Select
                id="pm-product-filter"
                value={vm.productFilter}
                onChange={(e) => vm.setProductFilter(e.target.value)}
              >
                <option value={PRODUCTFILTERALL}>All products</option>
                {vm.products.map((p) => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </Select>
            </div>
          </div>
          {canEdit ? (
            <Button
              variant="secondary"
              onClick={() => { setCloneNotice(null); setCloneOpen(true) }}
              disabled={vm.productFilter === PRODUCTFILTERALL || selectedIsBuy}
              title={
                vm.productFilter === PRODUCTFILTERALL
                  ? 'Select a product first'
                  : selectedIsBuy
                    ? 'A Buy product has no materials to clone'
                    : undefined
              }
            >
              Clone materials
            </Button>
          ) : null}
        </div>
      ) : null}

      {vm.isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Spinner />
        </div>
      ) : vm.items.length === 0 ? (
        <EmptyState />
      ) : (
        <>
          <Table
            items={vm.items}
            onOpen={setEditTarget}
            onAccept={(row) => void handleAccept(row)}
            onSavePreviewCost={vm.savePreviewCost}
            acceptingId={acceptingId}
          />
          <Paginator
            page={vm.page}
            pageSize={vm.pageSize}
            total={vm.total}
            onPageChange={vm.setPage}
            disabled={vm.isLoading}
          />
        </>
      )}

      <ProductVariantMaterialsEditModal
        target={editTarget}
        onClose={() => setEditTarget(null)}
        fetchDetail={vm.fetchDetail}
        syncMaterials={vm.syncMaterials}
      />

      <CloneMaterialsModal
        open={cloneOpen}
        productId={vm.productFilter === PRODUCTFILTERALL ? null : vm.productFilter}
        productName={selectedProductName}
        onClose={() => setCloneOpen(false)}
        listProductVariants={vm.listProductVariants}
        cloneMaterials={vm.cloneMaterials}
        onCloned={(n) => {
          setCloneOpen(false)
          setCloneNotice(
            n === 0
              ? 'No other variants to clone to.'
              : `Cloned materials to ${n} variant${n === 1 ? '' : 's'}.`
          )
          vm.reload()
        }}
      />
    </div>
  )
}

function EmptyState(): JSX.Element {
  return (
    <div className="text-center py-16 border border-border rounded-md bg-parchment/40">
      <p className="font-heading text-h3 text-charcoal">No product variants yet</p>
      <p className="font-body text-body-sm text-text-secondary mt-2">
        Add a product and propagate variants first, then come back here to
        link raw materials per variant.
      </p>
    </div>
  )
}

function formatPaisa(p: number | null): string {
  if (p === null) return '—'
  return `₹${(p / 100).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}

function Table({
  items,
  onOpen,
  onAccept,
  onSavePreviewCost,
  acceptingId
}: {
  readonly items: ProductVariantMaterialsSummaryDto[]
  readonly onOpen: (row: ProductVariantMaterialsSummaryDto) => void
  readonly onAccept: (row: ProductVariantMaterialsSummaryDto) => void
  readonly onSavePreviewCost: (
    variantId: string,
    previewCostPaisa: number
  ) => Promise<{ ok: true } | { ok: false; message: string }>
  readonly acceptingId: string | null
}): JSX.Element {
  const { can } = useAuth()
  const canEdit = can(SCREENCODES.PRODUCTPRICING, 'edit')
  return (
    <TableShell>
      <table className="w-full font-body text-body-sm">
        <thead className="bg-parchment/50 text-text-secondary">
          <tr>
            <Th>Product</Th>
            <Th>Variant</Th>
            <Th className="text-right">Baseline cost</Th>
            <Th className="text-right">Preview cost</Th>
            <Th className="text-right">Actions</Th>
            <Th className="text-right">Materials linked</Th>
          </tr>
        </thead>
        <tbody>
          {items.map((row) => (
            <MaterialsRow
              key={row.variant_id}
              row={row}
              canEdit={canEdit}
              onOpen={onOpen}
              onAccept={onAccept}
              onSavePreviewCost={onSavePreviewCost}
              acceptingId={acceptingId}
            />
          ))}
        </tbody>
      </table>
    </TableShell>
  )
}

/**
 * One variant row. Make variants link materials and Accept rolls the
 * computed cost up; Buy variants have no materials, so the Preview-cost
 * cell is an inline ₹ editor (Save writes the preview, Accept applies it)
 * and the Add-materials/Edit action is hidden.
 */
function MaterialsRow({
  row,
  canEdit,
  onOpen,
  onAccept,
  onSavePreviewCost,
  acceptingId
}: {
  readonly row: ProductVariantMaterialsSummaryDto
  readonly canEdit: boolean
  readonly onOpen: (row: ProductVariantMaterialsSummaryDto) => void
  readonly onAccept: (row: ProductVariantMaterialsSummaryDto) => void
  readonly onSavePreviewCost: (
    variantId: string,
    previewCostPaisa: number
  ) => Promise<{ ok: true } | { ok: false; message: string }>
  readonly acceptingId: string | null
}): JSX.Element {
  const isBuy = row.source_type === 'buy'
  const previewDiffers =
    (row.preview_baseline_cost_paisa ?? 0) !== (row.baseline_cost_paisa ?? 0)
  const acceptDisabled = !previewDiffers || acceptingId === row.variant_id
  const label = row.axes_label === null || row.axes_label === '' ? row.product_name : `${row.product_name} ${row.axes_label}`
  return (
    <tr className="border-t border-border">
      <Td>{row.product_name}</Td>
      <Td>
        {row.axes_label === null || row.axes_label === '' ? (
          <span className="text-text-secondary">default</span>
        ) : (
          row.axes_label
        )}
      </Td>
      <Td className="text-right whitespace-nowrap">{formatPaisa(row.baseline_cost_paisa)}</Td>
      <Td className="text-right whitespace-nowrap">
        {isBuy && canEdit ? (
          <div className="flex justify-end">
            <InlineRupeeEdit
              valuePaisa={row.preview_baseline_cost_paisa}
              onSave={(p) => onSavePreviewCost(row.variant_id, p)}
              ariaLabel={`Baseline cost for ${label} in rupees`}
              inputClassName="text-right"
            />
          </div>
        ) : (
          <span
            className={previewDiffers ? 'text-terra font-medium' : 'text-text-secondary'}
            title={
              previewDiffers
                ? 'Pending — differs from the accepted cost; click Accept to apply'
                : 'Matches the accepted cost'
            }
          >
            {formatPaisa(row.preview_baseline_cost_paisa)}
          </span>
        )}
      </Td>
      <Td className="text-right whitespace-nowrap">
        {canEdit ? (
          <div className="inline-flex gap-2">
            {isBuy ? null : (
              <Button size="sm" variant="secondary" onClick={() => onOpen(row)}>
                {row.material_count === 0 ? 'Add materials' : 'Edit'}
              </Button>
            )}
            <Button
              size="sm"
              onClick={() => onAccept(row)}
              disabled={acceptDisabled}
              title={
                acceptDisabled
                  ? 'No pending cost change to apply'
                  : isBuy
                    ? 'Apply the typed cost to this variant'
                    : 'Apply the saved materials’ cost to this variant'
              }
            >
              {acceptingId === row.variant_id ? 'Accepting…' : 'Accept'}
            </Button>
          </div>
        ) : null}
      </Td>
      <Td className="text-right">
        {row.material_count === 0 ? (
          <span className="text-text-secondary">none</span>
        ) : (
          <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-bamboo/20 text-charcoal font-medium text-body-xs">
            {row.material_count} material{row.material_count === 1 ? '' : 's'}
          </span>
        )}
      </Td>
    </tr>
  )
}
